import { v4 as uuid } from 'uuid';
import type {
  FormBuilder_EventFragment,
  FormBuilder_Event_Field,
  FormBuilder_NodeFragment,
  FormBuilder_ScreenNode,
} from '~/graphql/types';
import getFields from '../getFields';
import sortNodes from '../../sortNodes';
import isBlockWithOutput from '../../typeguards/isBlockWithOutput';
import getEventFieldForBlock from '../getEventFieldForBlock';

/**
 * Generate event fields for the submit event
 *
 * @param {Array<FormBuilder_NodeFragment>} nodes - Current nodes in the form builder
 * @param {FormBuilder_Event} event - Event
 * keywords: submitevent, eventfields, autogeneratedevent
 */
const generateEventFields = ({
  nodes,
  event,
}: {
  nodes: Array<FormBuilder_NodeFragment>;
  event: FormBuilder_EventFragment;
}): {
  // Updated event fields
  generatedEventFields: Array<FormBuilder_Event_Field>;

  // Lookup to find the matching event key
  eventKeyLookup: { [blockKey: string]: string };
} => {
  const prevEventFields = [...(event.fields || [])];
  const generatedEventFields: Array<FormBuilder_Event_Field> = [];
  const eventKeyLookup: { [blockKey: string]: string } = {};

  const sortedScreenNodes = sortNodes({ nodes }).filter(
    (node): node is FormBuilder_ScreenNode =>
      node.__typename === 'FormBuilder_ScreenNode',
  );

  sortedScreenNodes.forEach(screenNode => {
    screenNode.blocks.forEach(block => {
      if (!isBlockWithOutput(block)) return;

      const fields = getFields(block);
      if (!fields) return;

      const existingEventFieldForBlock = getEventFieldForBlock({
        blockKey: block.key,
        nodes,
        eventFields: prevEventFields,
      });

      /**
       * [IMPORTANT]
       * We want to keep the event field key the same as much as possible throughout its lifetime
       * so that the contact filters, flow conditions etc. still work for it.
       */
      const eventFieldKey = existingEventFieldForBlock
        ? existingEventFieldForBlock.key
        : uuid();

      eventKeyLookup[block.key] = eventFieldKey;

      const name = existingEventFieldForBlock
        ? existingEventFieldForBlock.name
        : `${screenNode.name} > ${block.label?.nl || block.label?.en}`;

      generatedEventFields.push({
        __typename: 'FormBuilder_Event_Field',
        key: eventFieldKey,
        name,
        ...fields,
      });
    });
  });

  return {
    generatedEventFields,
    eventKeyLookup,
  };
};

export default generateEventFields;
